import { Action, createReducer, on } from '@ngrx/store';
import {
	DegreeQuizGuideMeta,
	DegreeQuizQuestion,
	DegreeQuizOption,
	DegreeQuizSlug,
	DegreeQuizAnswerId,
} from '../../models/degree-quiz.models';
import {
	fetchQuizOptions,
	fetchQuizQuestions,
	fetchQuizResult,
	fetchSubjectGuides,
	initQuizAnswers,
	setQuizAnswer,
	updateDegreeQuizError,
	updateDegreeQuizResultId,
	updateLoading,
	updateQuizAnswers,
	updateQuizOptions,
	updateQuizQuestions,
	updateQuizResult,
	updateQuizSlug,
	updateQuizSubmitted,
	updateQuizVersion,
	updateSubjectGuides,
} from './degree-quiz.actions';
import { DegreeQuizResult, DegreeQuizVersion } from '@uc/web/core';

export const _STATE_DEGREE_QUIZ_FEATURE_KEY = 'stateDegreeQuiz';

export interface DegreeQuizState {
	guides: DegreeQuizGuideMeta[];
	quizOptions: DegreeQuizOption[];
	quizQuestions: DegreeQuizQuestion[];
	quizAnswers: DegreeQuizAnswerId[];
	quizSubmitted: boolean;
	quizVersion: DegreeQuizVersion;
	quizResult: DegreeQuizResult[];
	quizSlug: DegreeQuizSlug | null;
	resultId: string;
	isLoading: boolean;
	error: any;
}

export interface StateCourseSearchPartialState {
	readonly [_STATE_DEGREE_QUIZ_FEATURE_KEY]: DegreeQuizState;
}

export const initialState: DegreeQuizState = {
	guides: [],
	quizOptions: [],
	quizQuestions: [],
	quizAnswers: [],
	quizSubmitted: false,
	quizVersion: 'short',
	quizResult: [],
	quizSlug: null,
	resultId: '',
	isLoading: false,
	error: null,
};

const degreeQuizReducer = createReducer(
	initialState,
	on(updateDegreeQuizError, (state, { error }) => {
		return {
			...state,
			isLoading: false,
			error: error,
		};
	}),
	on(fetchSubjectGuides, fetchQuizOptions, fetchQuizResult, (state) => {
		return {
			...state,
			isLoading: true,
			error: null,
		};
	}),
	on(updateSubjectGuides, (state, { guides }) => {
		return {
			...state,
			guides,
			error: null,
			isLoading: false,
		};
	}),
	on(updateQuizOptions, (state, { options }) => {
		return {
			...state,
			quizOptions: options,
			error: null,
			isLoading: false,
		};
	}),
	on(fetchQuizQuestions, (state, { quizSlug }) => {
		return {
			...state,
			quizSlug,
			isLoading: true,
			error: null,
		};
	}),
	on(updateQuizQuestions, (state, { questions }) => {
		return {
			...state,
			quizQuestions: questions,
			isLoading: false,
			error: null,
		};
	}),
	on(initQuizAnswers, (state) => {
		const answers = Array(state.quizQuestions.length).fill(null);
		if (state.quizVersion === 'continuation') {
			state.quizAnswers.forEach((answer, index) => {
				answers[index] = answer;
			});
		}

		return {
			...state,
			quizAnswers: answers,
			error: null,
		};
	}),
	on(updateQuizAnswers, (state, { answers }) => {
		return {
			...state,
			quizAnswers: answers,
			error: null,
		};
	}),
	on(setQuizAnswer, (state, { index, answerId }) => {
		const quizAnswers = [...state.quizAnswers];
		quizAnswers[index] = answerId;
		return {
			...state,
			quizAnswers,
			error: null,
		};
	}),
	on(updateDegreeQuizResultId, (state, { resultId }) => {
		return {
			...state,
			resultId,
			error: null,
		};
	}),
	on(updateQuizResult, (state, { result }) => {
		return {
			...state,
			quizResult: result,
			isLoading: false,
			error: null,
		};
	}),
	on(updateQuizVersion, (state, { quizVersion }) => {
		return {
			...state,
			quizVersion,
			error: null,
		};
	}),
	on(updateQuizSlug, (state, { quizSlug }) => {
		return {
			...state,
			quizSlug,
			error: null,
		};
	}),
	on(updateQuizSubmitted, (state, { quizSubmitted }) => {
		return {
			...state,
			quizSubmitted,
			error: null,
		};
	}),
	on(updateLoading, (state, { isLoading }) => {
		return {
			...state,
			isLoading,
			error: null,
		};
	}),
);

export function reducer(state: DegreeQuizState | undefined, action: Action) {
	return degreeQuizReducer(state, action);
}
